import React, { useState } from "react";
import { Search, User, Heart, ShoppingBag, MapPin, ChevronDown, Menu, X, Phone } from "lucide-react";
import { mainNav, topPromos, brand } from "../mock";
import { useToast } from "../hooks/use-toast";

const PromoBar = () => (
  <div className="bg-[#171717] text-white text-[12px] tracking-wide overflow-hidden">
    <div className="marquee-track py-2">
      {[...topPromos, ...topPromos, ...topPromos].map((p, i) => (
        <span key={i} className="px-8 inline-flex items-center gap-3">
          <span>{p.label}</span>
          <button className="underline underline-offset-2 hover:text-[#f5c26b] transition-colors">
            {p.cta}
          </button>
          <span className="opacity-30">|</span>
        </span>
      ))}
    </div>
  </div>
);

const TopUtility = () => (
  <div className="hidden md:flex items-center justify-between text-[12px] text-neutral-600 border-b border-neutral-200 px-6 py-2">
    <div className="flex items-center gap-5">
      <a href="#" className="link-underline hover:text-[color:var(--brand-red)]">Inspiration</a>
      <a href="#" className="link-underline hover:text-[color:var(--brand-red)]">Gift Cards</a>
      <a href="#" className="link-underline hover:text-[color:var(--brand-red)]">Trade Program</a>
    </div>
    <div className="flex items-center gap-5">
      <a href={`tel:${brand.phone.replace(/[^0-9+]/g,"")}`} className="flex items-center gap-1.5 link-underline hover:text-[color:var(--brand-red)]">
        <Phone className="w-3.5 h-3.5" />
        <span className="font-medium">{brand.phone}</span>
      </a>
      <a href="#" className="link-underline hover:text-[color:var(--brand-red)]">Order Status</a>
      <a href="#" className="link-underline hover:text-[color:var(--brand-red)]">Help</a>
    </div>
  </div>
);

const Logo = () => (
  <a href="/" className="flex flex-col items-center leading-none group">
    <span className="font-serif text-[22px] md:text-[28px] font-semibold tracking-[0.02em] text-[color:var(--brand-ink)] group-hover:text-[color:var(--brand-red)] transition-colors">
      Inter Coastal
    </span>
    <span className="font-serif italic text-[13px] md:text-[15px] text-[color:var(--brand-red)] tracking-[0.25em] uppercase">
      Builders
    </span>
  </a>
);

const Header = ({ cartCount, wishCount, onCartOpen, onNavClick, onSignIn, onWishOpen }) => {
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    toast({ title: "Searching", description: `Results for "${q}"` });
    onNavClick?.("All");
  };

  return (
    <header className="sticky top-0 z-40 bg-white shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
      <PromoBar />
      <TopUtility />

      {/* Main header */}
      <div className="px-4 md:px-8 py-4 flex items-center gap-4">
        <button
          className="lg:hidden p-1 -ml-1"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        <div className="flex-shrink-0">
          <Logo />
        </div>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-2xl mx-auto">
          <div className="flex w-full items-center border border-neutral-300 rounded-full overflow-hidden focus-within:border-[color:var(--brand-red)] transition-colors">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="What can we help you find?"
              className="flex-1 px-5 py-2.5 text-sm outline-none bg-transparent"
            />
            <button type="submit" className="px-5 py-2.5 bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red-dark)] text-white transition-colors">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </form>

        <div className="flex items-center gap-4 md:gap-6 ml-auto">
          <div className="hidden md:flex items-center gap-1.5 text-[12px] text-neutral-700">
            <MapPin className="w-4 h-4 text-[color:var(--brand-red)]" />
            <div className="text-left leading-tight">
              <div className="font-medium">101 Palm Harbor Pkwy Suite B-116,</div>
              <div className="text-neutral-500">Palm Coast, FL 32137</div>
            </div>
          </div>
          <button onClick={onSignIn} className="hidden md:flex items-center gap-1.5 text-[12px] text-neutral-700 hover:text-[color:var(--brand-red)] transition-colors">
            <User className="w-5 h-5" />
            <div className="text-left leading-tight">
              <div className="font-medium">Sign In</div>
              <div className="text-neutral-500">My Account</div>
            </div>
          </button>
          <button onClick={onWishOpen} className="relative text-neutral-800 hover:text-[color:var(--brand-red)] transition-colors" aria-label="Wishlist">
            <Heart className="w-5 h-5 md:w-6 md:h-6" />
            {wishCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[color:var(--brand-red)] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {wishCount}
              </span>
            )}
          </button>
          <button onClick={onCartOpen} className="relative text-neutral-800 hover:text-[color:var(--brand-red)] transition-colors" aria-label="Cart">
            <ShoppingBag className="w-5 h-5 md:w-6 md:h-6" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-[color:var(--brand-red)] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile search */}
      <form onSubmit={submitSearch} className="md:hidden px-4 pb-3">
        <div className="flex w-full items-center border border-neutral-300 rounded-full overflow-hidden">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="What can we help you find?"
            className="flex-1 px-4 py-2 text-sm outline-none bg-transparent"
          />
          <button type="submit" className="px-4 py-2 bg-[color:var(--brand-red)] text-white">
            <Search className="w-4 h-4" />
          </button>
        </div>
      </form>

      {/* Category nav */}
      <nav className="hidden lg:block border-t border-neutral-200">
        <ul className="flex items-center justify-center gap-7 px-6 py-3 text-[13px] font-medium">
          {mainNav.map((item) => (
            <li key={item.label} className="group relative">
              <button
                onClick={() => onNavClick?.(item.label)}
                className={`flex items-center gap-1 py-1 link-underline hover:text-[color:var(--brand-red)] transition-colors ${item.accent ? "text-[color:var(--brand-red)]" : "text-neutral-800"}`}
              >
                {item.label}
                {item.featured && <ChevronDown className="w-3 h-3" />}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-4 border-b">
              <Logo />
              <button onClick={() => setMobileOpen(false)} aria-label="Close"><X className="w-6 h-6" /></button>
            </div>
            <ul className="py-2">
              {mainNav.map((n) => (
                <li key={n.label}>
                  <button
                    onClick={() => { onNavClick?.(n.label); setMobileOpen(false); }}
                    className={`w-full text-left px-5 py-3 border-b border-neutral-100 text-sm hover:bg-neutral-50 ${n.accent ? "text-[color:var(--brand-red)] font-medium" : ""}`}
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-5 py-4 space-y-2 text-sm">
              <a href={`tel:${brand.phone.replace(/[^0-9+]/g,"")}`} className="flex items-center gap-2 text-neutral-700">
                <Phone className="w-4 h-4" /> {brand.phone}
              </a>
              <div className="flex items-start gap-2 text-neutral-700">
                <MapPin className="w-4 h-4 mt-0.5 text-[color:var(--brand-red)]" />
                <span>101 Palm Harbor Pkwy Suite B-116,<br />Palm Coast, FL 32137</span>
              </div>
              <a href="#" className="block text-neutral-700">Sign In / Create Account</a>
              <a href="#" className="block text-neutral-700">Order Status</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
